import { ApplicationElement, html } from '@effectivastudio/lit-components';
import CommonActions from '@effectivastudio/lit-components/components/common_actions/element.js';

import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { ResizeController } from '@lit-labs/observers/resize-controller.js';
import { classMap } from 'lit/directives/class-map.js';

import '../card_header/element.js';
import '../card_prefooter/element.js';
import '../date_widget/element.js';
import '../slideshow/element.js';
import '../origin/element.js';

import '@effectivastudio/lit-components/components/collage/element.js';
import '@effectivastudio/lit-components/components/grid_list/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import '@effectivastudio/lit-components/components/list_item_10/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/img_placeholder/element.js';
import '@effectivastudio/lit-components/components/time_range/element.js';
import '@effectivastudio/lit-components/components/avatar_list/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Card element that consists of a header, body and footer.
 *
 * @prop uid - Id of the card
 * @prop cardType - Card type icon (options: event, date, discussion, engagement, marketplace, news, organize, poll)
 * @prop commentsOpen - Used for toggling comments show
 * @prop dateStart - Used in Event card as event start time and event details (optional)
 * @prop attendingState - Used in Event card for attending status (options: initial, going, cannot)
 * @prop isAnnouncement - Used to change card theme and Header look (optional)
 * @prop isFullView - Automatically set if card is in full view
 * @prop kind - Used to add label to the card, used in Marketplace and Engagement cards (options: offering, looking) (optional)
 * @prop lastPersonLike - Name of the last person that liked (only in Desktop version)
 * @prop gallery - Internal property => If present, indicates existence of collage; used for label position calculation
 * @prop labelPositionY - Internal property => Used to calculate label position depending on gallery or placeholder height
 * @prop slider - Internal property => If present, indicates existence of slider; used for label position calculation
 * @prop attachmentCount - Internal property => Number of attachments passed via "attachments" slot
 * @prop likeCount - Number of counts
 * @prop commentCount - Number of comments
 * @prop acceptedInvitationsCount - Number of accepted invitations
 * @prop declinedInvitationsCount - Number of declined invitations
 * @prop participantAvatars - Users we want to show as participants. (array of objects with name and picture properties)
 * @prop participantsUrl - URL of a page with the list of all participants
 * @prop ownerId - Card owner id
 * @prop ctaValue - Call to action status (optional)
 * @prop originName - Origin name for xs-origin
 * @prop originPicture - Origin picture for xs-origin (optional)
 * @prop originIcon - Origin Icon for xs-origin (optional)
 *
 * @slot header - For header content
 * @slot top - For gallery, slider and similar content; when empty, renders placeholder
 * @slot title - For title content
 * @slot body - For body content
 * @slot card-details - For Event and Marketplace card details
 * @slot main - For footer icons
 * @slot right - For footer dot menu
 * @slot comments - For card comments
 * @slot attachments - For card attachments
 *
 * @cssprop [--card-padding=16px] Element padding
 * @cssprop [--card-background: var(--contrast-light)] Element background
 * @cssprop [--card-font-color: var(--dark-text)] Element font color
 * @cssprop [--card-font-color-secondary: var(--dark-text-secondary)] Body text font color
 */

export default class Card extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    uid: { type: Number },
    cardType: { type: String },
    commentsOpen: { type: Boolean },
    dateStart: { type: Date },
    attendingState: { type: String },
    isAnnouncement: { type: Boolean, reflect: true },
    isFullView: { type: Boolean },
    kind: { type: String },
    lastPersonLike: { type: String },
    likeCount: { type: Number },
    commentCount: { type: Number },
    attachmentCount: { type: Number, state: true },
    acceptedInvitationsCount: { type: Number },
    declinedInvitationsCount: { type: Number },
    participantAvatars: { type: Array },
    participantsUrl: { type: String },
    ctaValue: { type: Boolean },
    ownerId: { type: Number },
    originName: { type: String },
    originPicture: { type: String },
    originIcon: { type: String },
    gallery: { state: true },
    labelPositionY: { state: true },
    slider: { state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.commonActions = new CommonActions(this);
    this.isFullView = /.+_cards\/\d+/.test(window.location.href);
  }

  connectedCallback() {
    super.connectedCallback();

    new ResizeController(this, {
      target: this,
      callback: entries => this.resizeHandler(entries),
    });

    new MutationController(this, {
      target: this.slotTop,
      config: { childList: true, subtree: false },
      callback: mutations => this.mutationHandler(mutations),
    });
  }

  /** @internal */
  get slotTop() {
    return this.querySelector("[slot='top']");
  }

  /** @internal */
  get placeholder() {
    return this.shadowRoot.querySelector('ef-img-placeholder');
  }

  /** @internal */
  get commentsContent() {
    return this.shadowRoot.querySelector('.comments');
  }

  /** @internal */
  get iconLike() {
    // TO DO: Implement with like handler
    return this.likeCount > 0 ? 'thumbs-up:solid' : 'thumbs-up';
  }

  /** @internal */
  get iconComment() {
    return this.commentCount && this.commentsOpen ? 'comment:solid' : 'comment';
  }

  /** @internal */
  get currentUser() {
    return this.AppConfig?.user?.id;
  }

  /** @internal */
  get isCardOwner() {
    return this.currentUser === this.ownerId;
  }

  /** @internal */
  get contactOwnerUrl() {
    return `/direct_messages/new?other_user_id=${this.ownerId}`;
  }

  /** @internal */
  get toggleCtaUrl() {
    return `/${this.cardType}_cards/${this.uid}/toggle_active`;
  }

  /** @internal */
  eventStatuses = {
    attend: 'going',
    cannot: 'cannot',
  };

  /** @internal */
  get attendUrl() {
    return `/event_cards/${this.uid}/attend`;
  }

  /** @internal */
  get canNotAttendUrl() {
    return `/event_cards/${this.uid}/cannot`;
  }

  /** @internal */
  get withdrawUrl() {
    return `/event_cards/${this.uid}/leave`;
  }

  /** @internal */
  get attendOn() {
    return this.attendingState !== this.eventStatuses.attend;
  }

  /** @internal */
  get canNotAttendOn() {
    return this.attendOn && this.attendingState !== this.eventStatuses.cannot;
  }

  /** @internal */
  get withdrawOn() {
    return !this.attendOn;
  }

  /** @internal */
  get contactOn() {
    return !this.isCardOwner && this.contactOwnerUrl && this.ctaValue;
  }

  /** @internal */
  get eventCardActions() {
    return [
      this.attendOn && this.getEventActionConfig(this.eventStatuses.attend),
      this.canNotAttendOn &&
        this.getEventActionConfig(this.eventStatuses.cannot),
      this.withdrawOn && this.getEventActionConfig(),
    ].filter(Boolean);
  }

  /** @internal */
  get engagementLabel() {
    return `engagement.toggle_${!!this.ctaValue}`;
  }

  /** @internal */
  get marketplaceLabel() {
    return `marketplace.toggle_${!!this.ctaValue}`;
  }

  /** @internal */
  get shouldRenderCtaItem() {
    return (
      (this.cardType === 'engagement' || this.cardType === 'marketplace') &&
      (this.contactOn || this.isCardOwner)
    );
  }

  renderCtaLabel() {
    return {
      marketplace: this.marketplaceLabel,
      engagement: this.engagementLabel,
    }[this.cardType];
  }

  getEventActionConfig(status) {
    let config;

    switch (status) {
      case this.eventStatuses.attend:
        config = {
          msg: this.t('events.snack.attend'),
          label: this.t('events.action.attend'),
          icon: 'check:solid',
          url: this.attendUrl,
          className: 'xs-btn xs-btn-lg btn-primary',
        };
        break;

      case this.eventStatuses.cannot:
        config = {
          msg: this.t('events.snack.cannot'),
          label: this.t('events.action.cannot'),
          icon: 'minus:solid',
          url: this.canNotAttendUrl,
          className: 'xs-btn xs-btn-lg btn-outline-primary',
        };
        break;

      default:
        config = {
          msg: this.t('events.snack.withdraw'),
          label: this.t('events.action.withdraw'),
          icon: 'ban:solid',
          url: this.withdrawUrl,
          className: 'xs-btn xs-btn-lg btn-outline-primary',
        };
    }

    return config;
  }

  onLikeClick() {
    alert('TO DO: Handle like click');
  }

  onCommentClick() {
    this.commentsOpen = !this.commentsOpen;
  }

  onAttachmentClick() {
    const attachmentHeader = this.shadowRoot.querySelector(
      '.attachment-list > h3'
    );

    attachmentHeader.scrollIntoView({ behavior: 'smooth' });

    attachmentHeader.classList.add('scrolled-to');
    setTimeout(() => {
      attachmentHeader.classList.remove('scrolled-to');
    }, 1000);
  }

  calculateLabelPosition() {
    const { gallery, slider, placeholder } = this;
    const element = gallery || slider || placeholder;

    let offset = 44;
    if (slider) {
      offset = 60;
    }

    return element.offsetHeight - offset;
  }

  resizeHandler() {
    this.labelPositionY = this.calculateLabelPosition();
  }

  mutationHandler() {
    this.gallery = this.querySelector('ef-collage');
    this.slider = this.querySelector('xs-slideshow');
  }

  authorizeUser() {
    if (this.currentUser) return true;
    this.commonActions.visit('/login');
  }

  async handleCtaToggle() {
    await this.commonActions.patch(this.toggleCtaUrl);
    this.ctaValue = !this.ctaValue;
  }

  async handleEventAttendAction(url, msg) {
    if (!this.authorizeUser()) return;

    try {
      const res = await this.commonActions.put(url);
      const resJSON = await res.json();
      this.acceptedInvitationsCount = resJSON.acceptedInvitationsCount;
      this.declinedInvitationsCount = resJSON.declinedInvitationsCount;
      this.attendingState = resJSON.state;
      this.commonActions.displaySnackBarSuccess(msg);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  handleCtaContact() {
    if (this.authorizeUser())
      this.commonActions.execJSFromResponse(this.contactOwnerUrl);
  }

  handleAttachmentSlotChange(e) {
    this.attachmentCount = e.target.assignedElements({ flatten: true }).length;
  }

  renderPlaceholder() {
    return html`<ef-img-placeholder icon="image"></ef-img-placeholder>`;
  }

  renderDateWidget() {
    return html`
      <xs-card-date-widget date=${this.dateStart} class="with-divider">
      </xs-card-date-widget>
    `;
  }

  renderLabel() {
    return html`
      <ef-label layout="md" style="top: ${this.labelPositionY}px">
        ${this.t(`kind.${this.kind}`)}
      </ef-label>
    `;
  }

  renderEventItems() {
    return html`
      <h3 class="subtitle">${this.t('events.participation')}</h3>

      <ef-grid-list
        class="event-actions"
        col="1"
        colSM="1"
        colMD="2"
        colLG="3"
        gap="large">
        ${this.eventCardActions.map(
          itemConfig => html`
            <button
              type="button"
              class=${itemConfig.className}
              @click=${() =>
                this.handleEventAttendAction(itemConfig.url, itemConfig.msg)}>
              <ef-icon name=${itemConfig.icon} class="prefix"></ef-icon>
              ${itemConfig.label}
            </button>
          `
        )}
      </ef-grid-list>
    `;
  }

  renderEventParticipants() {
    return html`
      <ef-avatar-list
        label
        .url=${this.participantsUrl}
        .users=${this.participantAvatars}
        .totalCount=${this.acceptedInvitationsCount}>
      </ef-avatar-list>
    `;
  }

  renderContactButton() {
    return html`
      <button
        @click=${this.handleCtaContact}
        type="button"
        class="xs-btn btn-primary">
        <ef-icon name="comment-alt-lines" class="prefix"></ef-icon>
        ${this.t('contact')}
      </button>
    `;
  }

  renderCtaToggleButton() {
    const config = {
      icon: this.ctaValue ? 'check:solid' : 'refresh:solid',
      label: this.renderCtaLabel(),
      className: this.ctaValue ? 'btn-primary' : 'btn-secondary',
    };

    return html`
      <button
        @click=${this.handleCtaToggle}
        type="button"
        class="xs-btn ${config.className}">
        <ef-icon name=${config.icon} class="prefix"> </ef-icon>
        ${this.t(config.label)}
      </button>
    `;
  }

  renderCtaItem() {
    return html`
      <div class="card-actions">
        ${this.isCardOwner
          ? this.renderCtaToggleButton()
          : this.renderContactButton()}
      </div>
    `;
  }

  renderPrefooter() {
    return html`
      <xs-card-prefooter
        commentCount=${this.commentCount}
        likeCount=${this.likeCount}
        lastPersonLike=${this.lastPersonLike}>
      </xs-card-prefooter>
    `;
  }

  renderOrigin() {
    return html`
      <xs-origin
        border="true"
        .picture=${this.originPicture}
        .icon=${this.originIcon}>
        <span class="small-label">${this.originName}</span>
      </xs-origin>
    `;
  }

  render() {
    const classes = {
      truncate: !this.isFullView,
    };

    return html`
      <slot name="header"></slot>

      <div class="body">
        ${this.cardType === 'event' ? this.renderDateWidget() : null}

        <slot name="top">
          ${this.gallery || this.slider ? null : this.renderPlaceholder()}
        </slot>

        ${this.kind ? this.renderLabel() : null}
        ${this.isFullView ? this.renderOrigin() : null}

        <slot name="title"></slot>

        <slot name="card-details"></slot>

        <slot name="body" class=${classMap(classes)}></slot>

        <section class="attachment-list" ?hidden=${!this.attachmentCount}>
          <h3 class="xs-title-3">${this.t('attachments')}</h3>
          <slot
            name="attachments"
            @slotchange=${this.handleAttachmentSlotChange}>
          </slot>
        </section>

        ${this.cardType === 'event' ? this.renderEventItems() : null}
        ${this.cardType === 'event' && this.acceptedInvitationsCount
          ? this.renderEventParticipants()
          : null}
        ${this.shouldRenderCtaItem ? this.renderCtaItem() : null}
      </div>

      ${this.commentCount || this.likeCount ? this.renderPrefooter() : null}

      <footer>
        <ef-list-item-10>
          <div slot="main">
            <ef-grid-list col="0" gap="large">
              <button @click=${this.onLikeClick} aria-label="like">
                <ef-icon name=${this.iconLike}></ef-icon>
                <span class="count" ?hidden=${!this.likeCount}>
                  ${this.likeCount}
                </span>
              </button>

              <button @click=${this.onCommentClick} aria-label="comment">
                <ef-icon name=${this.iconComment}> </ef-icon>
                <span class="count" ?hidden=${!this.commentCount}>
                  ${this.commentCount}
                </span>
              </button>

              ${this.attachmentCount
                ? html`
                    <button
                      @click=${this.onAttachmentClick}
                      aria-label="upload file">
                      <ef-icon name="paperclip"> </ef-icon>
                      <span class="count">${this.attachmentCount}</span>
                    </button>
                  `
                : null}
            </ef-grid-list>
          </div>
          <div slot="right">
            <xs-btn-more></xs-btn-more>
          </div>
        </ef-list-item-10>

        <details class="comments" ?open=${this.commentsOpen}>
          <summary></summary>
          <slot name="comments"></slot>
        </details>
      </footer>
    `;
  }
}

Card.registerTagName('xs-card');

customElements.get('xs-card') || customElements.define('xs-card', Card);
